/**
 * NESW — Canonical add-to-cart helper (single source of truth).
 *
 * Every add-to-cart surface in the theme (PDP form, card quick-adds, sticky
 * mobile CTA, cart-drawer / cart-page upsells, lookbook, wishlist, etc.) MUST
 * call window.neswAddToCart() instead of doing its own fetch('/cart/add.js').
 *
 * Why: Meta's structured `AddToCart` is emitted by Shopify's platform when a
 * product is added via the Ajax cart API using the canonical request shape
 * `{ items: [{ id, quantity }] }` followed by a real cart reconcile. Divergent
 * implementations (legacy `{ id, quantity }` body, page reloads before the
 * event fires, no cart:change dispatch) silently drop that event. Funnel one
 * helper so every surface behaves identically to the surfaces that already work.
 *
 * Deliberately fires NO fbq / Shopify.analytics.publish itself — emission stays
 * solely with Shopify's channel, so one successful add = exactly one AddToCart
 * (no double counting).
 *
 * @param {number|string} variantId  Shopify variant id to add.
 * @param {number}        [quantity=1]
 * @param {Object}        [opts]
 * @param {boolean}       [opts.openDrawer]      open the cart drawer on success
 * @param {boolean}       [opts.refreshCartPage] in-place refresh of the cart page
 * @returns {Promise<Object>} resolves with the fresh /cart.js cart on success;
 *          rejects with { status, body } on a failed add (sold-out 422, or the
 *          cart-limit guard's blocked 422) so callers show an error, not "Added".
 */
(function () {
  if (window.neswAddToCart) return; // define once

  window.neswAddToCart = function (variantId, quantity, opts) {
    opts = opts || {};
    quantity = parseInt(quantity, 10) || 1;
    var id = parseInt(variantId, 10);

    if (!id) {
      return Promise.reject({ status: 0, body: { description: 'Missing variant id' } });
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: quantity }] }) // canonical shape — required for AddToCart
    })
      .then(function (r) {
        // Reject sold-out / over-limit (incl. the cart-limit guard's blocked 422)
        // so callers never show a false "Added!" and we never dispatch on a failed add.
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (e) {
            return Promise.reject({ status: r.status, body: e });
          });
        }
        return r.json();
      })
      // Reconcile against the authoritative cart so counts/UI never drift.
      .then(function () {
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:change', { detail: cart }));
        if (opts.openDrawer && typeof window.openCartDrawer === 'function') {
          window.openCartDrawer();
        }
        if (opts.refreshCartPage && typeof window.refreshCartPage === 'function') {
          window.refreshCartPage();
        }
        return cart;
      });
  };

  /**
   * Batched add — adds MANY line items in a SINGLE /cart/add.js request, then one
   * reconcile + one `cart:change`. Use for bundles so the whole pack lands at once
   * (instant) instead of one-at-a-time, and the drawer re-renders exactly once
   * (no per-item race that can leave the totals unpainted).
   *
   * @param {Array<{id:(number|string), quantity:number}>} items
   * @param {Object} [opts]  same as neswAddToCart (openDrawer / refreshCartPage)
   * @returns {Promise<Object>} fresh /cart.js cart; rejects { status, body } on failure.
   */
  window.neswAddToCartItems = function (items, opts) {
    opts = opts || {};
    var payload = (items || [])
      .map(function (it) {
        var o = { id: parseInt(it.id, 10), quantity: parseInt(it.quantity, 10) || 1 };
        // Pass line-item properties through (used to tag bundle lines with a
        // shared `_bundle_id` + `_bundle_role` so the cart drawer can lock a
        // bundle together). Leading-underscore keys stay hidden at checkout.
        if (it.properties && typeof it.properties === 'object') o.properties = it.properties;
        return o;
      })
      .filter(function (it) { return it.id; });

    if (!payload.length) {
      return Promise.reject({ status: 0, body: { description: 'No items to add' } });
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: payload }) // canonical shape, all lines at once
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (e) {
            return Promise.reject({ status: r.status, body: e });
          });
        }
        return r.json();
      })
      .then(function () {
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:change', { detail: cart }));
        if (opts.openDrawer && typeof window.openCartDrawer === 'function') {
          window.openCartDrawer();
        }
        if (opts.refreshCartPage && typeof window.refreshCartPage === 'function') {
          window.refreshCartPage();
        }
        return cart;
      });
  };
})();
